import { Alert, Badge, Button, Card } from '../shared/components';

import styles from './ScreenLayout.module.css';
import overviewStyles from './OverviewPage.module.css';

/**
 * Where a linked employee lands.
 *
 * Signing in used to drop people straight into an edit form, which reads as a
 * demand before it reads as a record. This says who the school has them down
 * as, what is left to do, and where to do it.
 */

export interface OverviewFact {
  readonly labelAr: string;
  readonly valueAr: string;
}

export interface OverviewTask {
  readonly id: string;
  readonly labelAr: string;
  /** What remains, or null when this task is finished. */
  readonly remainingAr: string | null;
  readonly onGo: () => void;
}

export interface OverviewPageProps {
  readonly fullName: string;
  readonly facts: readonly OverviewFact[];
  readonly tasks: readonly OverviewTask[];
  /** The phase the fund is in, in the committee's own words. */
  readonly phaseAr: string;
  readonly phaseNoteAr: string | null;
}

export function OverviewPage({
  fullName,
  facts,
  tasks,
  phaseAr,
  phaseNoteAr,
}: OverviewPageProps) {
  const outstanding = tasks.filter((task) => task.remainingAr !== null);
  const next = outstanding[0];

  return (
    <div className={styles.page}>
      <h1 className="visually-hidden">ملفي</h1>

      <Card title={fullName} description={phaseAr}>
        {phaseNoteAr ? <Alert severity="info">{phaseNoteAr}</Alert> : null}

        {outstanding.length === 0 ? (
          <Alert severity="success" title="اكتمل ما هو مطلوب منك">
            لا يوجد شيء ناقص. ستصلك رسالة إذا احتاجت اللجنة أي إضافة.
          </Alert>
        ) : (
          <Alert severity="warning" title={`متبقٍ ${String(outstanding.length)} من المهام`}>
            <div className={overviewStyles.nextRow}>
              <span>{`ابدأ بـ: ${next?.labelAr ?? ''}`}</span>
              {next ? (
                <Button type="button" size="sm" onClick={next.onGo}>
                  المتابعة
                </Button>
              ) : null}
            </div>
          </Alert>
        )}

        {/* What the school already has. Shown so it can be checked and
            reported if wrong — it is not the employee's to edit here. */}
        <dl className={overviewStyles.facts}>
          {facts.map((fact) => (
            <div key={fact.labelAr} className={overviewStyles.fact}>
              <dt className={overviewStyles.factLabel}>{fact.labelAr}</dt>
              <dd className={overviewStyles.factValue}>{fact.valueAr}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card title="ما هو مطلوب منك" description="اضغط على أي بند للانتقال إليه">
        <ul className={overviewStyles.tasks}>
          {tasks.map((task) => (
            <li key={task.id} className={overviewStyles.task}>
              <div className={overviewStyles.taskText}>
                <span className={overviewStyles.taskLabel}>{task.labelAr}</span>
                <span className={overviewStyles.taskDetail}>
                  {task.remainingAr ?? 'مكتمل'}
                </span>
              </div>

              <div className={overviewStyles.taskActions}>
                {/* §4.2 — state is carried by the word, not by the colour. */}
                <Badge tone={task.remainingAr === null ? 'success' : 'warning'} withDot>
                  {task.remainingAr === null ? 'مكتمل' : 'ناقص'}
                </Badge>
                <Button type="button" variant="ghost" size="sm" onClick={task.onGo}>
                  فتح
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
